// src/components/prestige/AlertBell.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell, Check, CheckCheck, Banknote, PenLine, PackageOpen, Shield } from "lucide-react";
import { Drawer, Badge, Button, Spin, message } from "antd";
import axios from "../../utils/axiosDash";

const POLL_MS = 60000;

const SOURCE_META = {
  cashier: { label: "Cashier", icon: Banknote, color: "#DC2626", soft: "#FEF2F2" },
  writer: { label: "Writer", icon: PenLine, color: "#B45309", soft: "#FFFBEB" },
  storeroom: { label: "Store room", icon: PackageOpen, color: "#2F6FED", soft: "#EFF4FF" },
  hygiene: { label: "Hygiene", icon: Shield, color: "#7C3AED", soft: "#F5F3FF" },
};

// Shown exactly as stored (UTC), same as the alerts page
const formatWhen = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-IN", { timeZone: "UTC", day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("en-IN", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${day}, ${time}`;
};

const keyOf = (a) => `${a.sourceKey}:${a.id}`;

/**
 * Props
 *  - storeName:   selected store ("All Stores" or a store name)
 *  - username:    logged-in user (read state is stored per user)
 *  - onOpenAlert: (alert) => void   called when an alert is clicked
 */
export default function AlertBell({ storeName, username, onOpenAlert }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  const fetchUnread = useCallback(
    async (silent = true) => {
      if (!username) return;
      const id = ++reqRef.current;
      if (!silent) setLoading(true);
      try {
        const res = await axios.post("/get-empire-unread-alerts", { storeName, username });
        if (id !== reqRef.current) return;
        if (res.data?.success) {
          setItems(res.data.items || []);
          setCount(res.data.unreadCount || 0);
        }
      } catch (err) {
        console.error("Error fetching unread alerts:", err);
      } finally {
        if (id === reqRef.current) setLoading(false);
      }
    },
    [storeName, username]
  );

  // Initial load + polling
  useEffect(() => {
    fetchUnread(true);
    const t = setInterval(() => fetchUnread(true), POLL_MS);
    return () => clearInterval(t);
  }, [fetchUnread]);

  // Fresh list every time the drawer opens
  useEffect(() => {
    if (open) fetchUnread(false);
  }, [open, fetchUnread]);

  const markRead = async (targets) => {
    if (!targets.length) return;
    const gone = new Set(targets.map(keyOf));
    setItems((prev) => prev.filter((i) => !gone.has(keyOf(i))));
    setCount((c) => Math.max(0, c - targets.length));
    try {
      await axios.post("/mark-empire-alerts-read", {
        username,
        storeName,
        alerts: targets.map(({ sourceKey, id }) => ({ sourceKey, id })),
      });
    } catch (err) {
      message.error("Could not mark as read");
      fetchUnread(true);
    }
  };

  const markAll = async () => {
    setItems([]);
    setCount(0);
    try {
      await axios.post("/mark-empire-alerts-read", { username, storeName, markAll: true });
    } catch (err) {
      message.error("Could not mark all as read");
      fetchUnread(true);
    }
  };

  const handleOpenAlert = (alert) => {
    markRead([alert]);
    setOpen(false);
    onOpenAlert?.(alert);
  };

  return (
    <>
      <Badge count={count} overflowCount={99} size="small" offset={[-2, 2]}>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 transition-all"
          title="Alerts"
          aria-label={`Unread alerts: ${count}`}
        >
          <Bell size={18} />
        </button>
      </Badge>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width={400}
        title={
          <div className="flex items-center gap-2">
            <span>Unread alerts</span>
            {count > 0 && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                {count}
              </span>
            )}
          </div>
        }
        extra={
          <Button size="small" icon={<CheckCheck size={14} />} onClick={markAll} disabled={!items.length}>
            Mark all as read
          </Button>
        }
        styles={{ body: { padding: 0 } }}
      >
        {loading && !items.length ? (
          <div className="flex h-40 items-center justify-center">
            <Spin />
          </div>
        ) : !items.length ? (
          <div className="flex h-60 flex-col items-center justify-center gap-2 text-slate-400">
            <Bell size={28} />
            <p className="text-sm">You're all caught up</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((a) => {
              const meta = SOURCE_META[a.sourceKey] || SOURCE_META.cashier;
              const Icon = meta.icon;
              return (
                <li
                  key={keyOf(a)}
                  onClick={() => handleOpenAlert(a)}
                  className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <span
                    className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ background: meta.soft, color: meta.color }}
                  >
                    <Icon size={16} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold capitalize leading-snug text-slate-900">
                      {a.message}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {meta.label}
                      {a.store ? ` · ${a.store}` : ""}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">{formatWhen(a.time)}</p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // don't navigate
                      markRead([a]);
                    }}
                    className="flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
                    title="Mark as read"
                  >
                    <Check size={12} />
                    Read
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Drawer>
    </>
  );
}